import React from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { Box, Tooltip, Typography } from '@mui/material';
import { ArrowDownward, ArrowUpward, InfoOutlined } from '@mui/icons-material';
import {
  bodyFont,
  formatCount,
  headingFont,
  ink,
  negative,
  neutral,
  positive,
  surface
} from './tokens';

/** Signed percentage change, or null when there is no basis for comparison. */
interface DeltaProps {
  change: number | null;
  /** What the change is measured against, e.g. "vs previous 30 days". */
  basis: string;
  /** True when a fall is the good outcome. No such metric here yet, but the
   *  tiles are reused and a hard-coded "up is green" would be a trap. */
  invert?: boolean;
}

const Delta: React.FC<DeltaProps> = ({ change, basis, invert }) => {
  if (change === null) {
    return (
      <Typography sx={{ fontFamily: bodyFont, fontSize: 13, color: ink.faint }}>
        No {basis.replace(/^vs /, '')} to compare
      </Typography>
    );
  }

  const flat = Math.abs(change) < 0.5;
  const rising = change > 0;
  const good = invert ? !rising : rising;
  const palette = flat ? neutral : good ? positive : negative;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          height: 22,
          px: '7px',
          borderRadius: '99px',
          backgroundColor: palette.bg,
          color: palette.fg,
          fontFamily: bodyFont,
          fontWeight: 600,
          fontSize: 12,
          flex: 'none'
        }}
      >
        {!flat &&
          (rising ? (
            <ArrowUpward sx={{ fontSize: 13 }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 13 }} />
          ))}
        {flat ? 'Flat' : `${Math.abs(change).toFixed(Math.abs(change) < 10 ? 1 : 0)}%`}
      </Box>
      <Typography noWrap sx={{ fontFamily: bodyFont, fontSize: 13, color: ink.faint }}>
        {basis}
      </Typography>
    </Box>
  );
};

interface StatTileProps {
  label: string;
  value: number;
  /** Sits beside the figure, e.g. "of 340 issued". */
  caption?: string;
  hint?: string;
  delta?: DeltaProps;
  /** Per-bucket values for the sparkline; omit for a tile with no trend. */
  trend?: number[];
  color: string;
}

/**
 * A headline figure in the KPI strip: label, number, change against the
 * comparison window, and the shape of the period behind it.
 */
export const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  caption,
  hint,
  delta,
  trend,
  color
}) => {
  const sparkline = (trend ?? []).map((count, index) => ({ index, count }));
  const hasShape = sparkline.length > 1 && sparkline.some((point) => point.count > 0);
  const gradientId = `spark-${label.replace(/[^a-z]/gi, '').toLowerCase()}`;

  return (
    <Box
      sx={{
        backgroundColor: surface.card,
        border: `1px solid ${surface.border}`,
        borderRadius: '16px',
        p: '18px 20px 0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: 0,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Typography
          sx={{
            fontFamily: bodyFont,
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: '.09em',
            textTransform: 'uppercase',
            color: ink.faint
          }}
        >
          {label}
        </Typography>
        {hint && (
          <Tooltip title={hint} placement='top' arrow enterTouchDelay={0}>
            <InfoOutlined sx={{ fontSize: 14, color: ink.faint }} />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', mt: '10px' }}>
        <Typography
          sx={{
            fontFamily: headingFont,
            fontWeight: 700,
            fontSize: 32,
            lineHeight: 1,
            letterSpacing: '-.02em',
            color: ink.strong
          }}
        >
          {formatCount(value)}
        </Typography>
        {caption && (
          <Typography sx={{ fontFamily: bodyFont, fontSize: 13, color: ink.muted }}>
            {caption}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: '12px', minHeight: 22 }}>{delta && <Delta {...delta} />}</Box>

      {/* The sparkline bleeds to the tile's edges, so it reads as the metric's
          own shape rather than as a chart that happens to sit inside a card. */}
      <Box sx={{ height: 44, mt: '14px', mx: '-20px' }}>
        {hasShape && (
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={sparkline} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={color} stopOpacity={0.22} />
                  <stop offset='100%' stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type='monotone'
                dataKey='count'
                stroke={color}
                strokeWidth={1.75}
                fill={`url(#${gradientId})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default StatTile;
